import { useId } from 'react';
import type { WebsiteRegionFieldState, WebsiteSlotSchema } from '@dukanti/shared';
import { Label } from '@/components/ui/label';
import type { RegionEditor } from '../hooks/use-region-editor';
import { useReferenceOptions, type ReferenceKind } from '../hooks/use-reference-options';
import { useWebsiteT } from '../hooks/use-website-t';
import { slotFieldLabel } from '../lib/region-labels';
import { authoredRefId } from '../lib/slot-values';
import { NativeSelect } from './native-select';

export interface RegionReferenceFormProps {
  readonly editor: RegionEditor;
  readonly storeId: string;
}

const CHOOSE_KEYS: Readonly<Record<ReferenceKind, string>> = {
  category: 'admin.websiteTemplates.ref.chooseCategory',
  product: 'admin.websiteTemplates.ref.chooseProduct',
  'custom-collection': 'admin.websiteTemplates.ref.chooseCollection',
};

function kindOf(slot: WebsiteSlotSchema): ReferenceKind {
  if (slot.type === 'product-ref') return 'product';
  if (slot.type === 'custom-collection-ref') return 'custom-collection';
  return 'category';
}

function stateFor(kind: ReferenceKind, id: string): WebsiteRegionFieldState {
  // Nothing chosen is "choose automatically": the template picks.
  if (id === '') return { mode: 'default' };
  if (kind === 'category') return { mode: 'authored', value: { v: { categoryId: id } } };
  if (kind === 'product') return { mode: 'authored', value: { v: { productId: id } } };
  return { mode: 'authored', value: { v: { collectionRef: id } } };
}

/**
 * A reference region picks one of the store's own categories, products or
 * collections, or lets the template choose. The lists come from the modules
 * that own them; nothing here creates or edits a category.
 */
export function RegionReferenceForm({
  editor,
  storeId,
}: RegionReferenceFormProps): React.JSX.Element | null {
  const { t, lang } = useWebsiteT();
  const id = useId();
  const slot = editor.slots[0];
  const state = slot ? editor.values[slot.id] : undefined;
  const kind = slot ? kindOf(slot) : 'category';
  const references = useReferenceOptions(kind, storeId, lang);
  if (!slot || !state) return null;

  const issueKey = editor.fieldIssues[slot.id];
  return (
    <div className="space-y-1.5" data-testid={`field-${slot.id}`}>
      <Label htmlFor={id}>{slotFieldLabel(t, slot)}</Label>
      {references.options.length === 0 && !references.isLoading ? (
        <p className="text-muted-foreground text-xs">{t('admin.websiteTemplates.ref.noOptions')}</p>
      ) : (
        <NativeSelect
          id={id}
          value={state.mode === 'authored' ? authoredRefId(state) : ''}
          placeholder={t('admin.websiteTemplates.ref.auto')}
          options={references.options}
          aria-label={t(CHOOSE_KEYS[kind])}
          aria-invalid={issueKey !== undefined ? true : undefined}
          onChange={(value) => editor.setField(slot.id, stateFor(kind, value))}
        />
      )}
      {issueKey === undefined ? null : (
        <p role="alert" className="text-destructive text-xs">
          {t(issueKey)}
        </p>
      )}
    </div>
  );
}
